import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	STUDIO_FLOW,
	STUDIO_OPTIONS_BY_STEP,
	STUDIO_UI_FLOW,
} from '../constants/studioOptions';
import {
	pickRandomCity,
	suggestLocationFromEthnicity,
} from '../constants/studioLocationSuggestions';
import { STUDIO_NAME_OPTIONS } from '../constants/studioFinalOptions';
import storageManager from '@/api/storageManager';

const INITIAL_SELECTIONS = {
	gender: 'woman',
	ethnicity: '',
	hairstyle: '',
	haircolor: '',
	bodytype: '',
	eyecolor: '',
	intimatesize: '',
};

const INITIAL_FINAL = {
	name: 'Sofia',
	personality: 'Romantic',
	occupation: 'Student',
	relationship: 'Single',
	nationality: 'American',
	city: pickRandomCity('American'),
	zodiac: 'Libra',
	kinks: ['Teasing'],
	voice: 'Voice 4',
};

const ageToDecade = (age) => {
	if (age <= 21) return '20s';
	if (age <= 29) return '20s';
	if (age <= 39) return '30s';
	if (age <= 49) return '40s';
	if (age <= 59) return '50s';
	return '60s';
};

const sanitizeStudioFirstName = (name) => {
	if (!name || typeof name !== 'string') {
		return '';
	}

	return name.trim().split(/\s+/)[0] ?? '';
};

const ensureNameValue = (currentValue, names, fallbackValue) => {
	const sanitized = sanitizeStudioFirstName(currentValue);

	if (!names?.length) {
		return sanitized || sanitizeStudioFirstName(fallbackValue);
	}

	if (names.includes(sanitized)) {
		return sanitized;
	}

	return sanitizeStudioFirstName(fallbackValue) || names[0] || sanitized;
};

const isInitialFinalDetails = (details) =>
	details.name === INITIAL_FINAL.name &&
	details.personality === INITIAL_FINAL.personality &&
	details.occupation === INITIAL_FINAL.occupation &&
	details.relationship === INITIAL_FINAL.relationship &&
	details.nationality === INITIAL_FINAL.nationality &&
	details.city === INITIAL_FINAL.city &&
	details.zodiac === INITIAL_FINAL.zodiac &&
	details.voice === INITIAL_FINAL.voice &&
	JSON.stringify(details.kinks) === JSON.stringify(INITIAL_FINAL.kinks);

const ensureOptionValue = (currentValue, options, fallbackValue) => {
	if (!options?.length) {
		return currentValue;
	}

	if (options.some((option) => option.value === currentValue)) {
		return currentValue;
	}

	return fallbackValue ?? options[0]?.value ?? currentValue;
};

const ensureKinkValues = (currentValues, options, fallbackValues) => {
	if (!options?.length) {
		return currentValues;
	}

	const validValues = currentValues.filter((value) =>
		options.some((option) => option.value === value),
	);

	if (validValues.length > 0) {
		return validValues;
	}

	if (fallbackValues?.length) {
		return fallbackValues;
	}

	return options[0]?.value ? [options[0].value] : currentValues;
};

const buildInitialState = () => {
	storageManager.studio.consumeRestorePending();
	const savedState = storageManager.studio.getState();

	if (!savedState) {
		return {
			step: 'gender',
			gender: 'woman',
			age: 25,
			selections: INITIAL_SELECTIONS,
			finalDetails: INITIAL_FINAL,
			shouldApplyQuizDefaults: true,
		};
	}

	return {
		step: savedState.step || 'gender',
		gender: savedState.gender || 'woman',
		age: savedState.age || 25,
		selections: { ...INITIAL_SELECTIONS, ...savedState.selections },
		finalDetails: {
			...INITIAL_FINAL,
			...savedState.finalDetails,
			name: sanitizeStudioFirstName(
				savedState.finalDetails?.name ?? INITIAL_FINAL.name,
			),
		},
		shouldApplyQuizDefaults: isInitialFinalDetails({
			...INITIAL_FINAL,
			...savedState.finalDetails,
			name: sanitizeStudioFirstName(
				savedState.finalDetails?.name ?? INITIAL_FINAL.name,
			),
		}),
	};
};

export const useStudioWizard = (language = 'en', quizOptions) => {
	const initialState = useMemo(() => buildInitialState(), []);
	const shouldApplyQuizDefaultsRef = useRef(
		initialState.shouldApplyQuizDefaults,
	);
	const defaultsAppliedRef = useRef(false);
	const [step, setStep] = useState(initialState.step);
	const [gender, setGender] = useState(initialState.gender);
	const [age, setAge] = useState(initialState.age);
	const [selections, setSelections] = useState(initialState.selections);
	const [finalDetails, setFinalDetails] = useState(initialState.finalDetails);
	const [portraitFile, setPortraitFile] = useState(null);
	const [portraitPrompts, setPortraitPrompts] = useState(null);

	useEffect(() => {
		storageManager.studio.setState({
			step,
			gender,
			age,
			selections,
			finalDetails,
		});
	}, [step, gender, age, selections, finalDetails]);

	useEffect(() => {
		if (!quizOptions || defaultsAppliedRef.current) {
			return;
		}

		const { defaults } = quizOptions;

		setFinalDetails((prev) => {
			const next = { ...prev };

			if (shouldApplyQuizDefaultsRef.current && defaults) {
				next.name = sanitizeStudioFirstName(defaults.name ?? prev.name);
				next.personality = defaults.personality ?? prev.personality;
				next.occupation = defaults.occupation ?? prev.occupation;
				next.relationship = defaults.relationship ?? prev.relationship;
				next.nationality = defaults.nationality ?? prev.nationality;
				next.zodiac = defaults.zodiac ?? prev.zodiac;
				next.kinks = defaults.kinks?.length ? defaults.kinks : prev.kinks;
				next.voice = defaults.voice ?? prev.voice;
			}

			next.personality = ensureOptionValue(
				next.personality,
				quizOptions.personality,
				defaults?.personality,
			);
			next.occupation = ensureOptionValue(
				next.occupation,
				quizOptions.occupation,
				defaults?.occupation,
			);
			next.relationship = ensureOptionValue(
				next.relationship,
				quizOptions.relationship,
				defaults?.relationship,
			);
			next.nationality = ensureOptionValue(
				next.nationality,
				quizOptions.nationality,
				defaults?.nationality,
			);
			next.zodiac = ensureOptionValue(
				next.zodiac,
				quizOptions.zodiac,
				defaults?.zodiac,
			);
			next.voice = ensureOptionValue(
				next.voice,
				quizOptions.voice,
				defaults?.voice,
			);
			next.kinks = ensureKinkValues(
				next.kinks,
				quizOptions.kinks,
				defaults?.kinks,
			);
			next.name = ensureNameValue(next.name, quizOptions.names, defaults?.name);

			if (shouldApplyQuizDefaultsRef.current && next.nationality) {
				next.city = pickRandomCity(next.nationality);
			}

			return next;
		});

		defaultsAppliedRef.current = true;
	}, [quizOptions]);

	const flowIndex = STUDIO_UI_FLOW.indexOf(step);
	const isFirstStep = flowIndex <= 0;
	const hideNav = false;
	const canGoBack =
		step !== 'gender' &&
		(STUDIO_FLOW.includes(step) || step === 'generating' || step === 'result');

	const optionsForStep = useCallback(
		(stepKey) => {
			const pool = STUDIO_OPTIONS_BY_STEP[stepKey];
			if (!pool) return [];
			if (stepKey === 'intimatesize') {
				return pool[gender] ?? [];
			}
			return pool[gender] ?? [];
		},
		[gender],
	);

	const goToStep = (nextStep, nextGender = gender) => {
		if (nextStep === 'gender') {
			setStep('gender');
			return;
		}

		if (nextGender !== gender) {
			setGender(nextGender);
			setSelections({ ...INITIAL_SELECTIONS, gender: nextGender });
		}
		setStep(nextStep);
	};

	const selectGender = (value) => {
		if (value !== gender) {
			setGender(value);
			setSelections({ ...INITIAL_SELECTIONS, gender: value });
		}
		goToStep('ethnicity', value);
	};

	const getNextStepAfter = (stepKey) => {
		const index = STUDIO_FLOW.indexOf(stepKey);
		return STUDIO_FLOW[index + 1] ?? stepKey;
	};

	const applyLocationFromEthnicity = (ethnicity) => {
		const { nationality, city } = suggestLocationFromEthnicity(ethnicity);
		setFinalDetails((prev) => ({ ...prev, nationality, city }));
	};

	const selectOption = (stepKey, label) => {
		setSelections((prev) => ({ ...prev, [stepKey]: label }));
		if (stepKey === 'ethnicity') {
			applyLocationFromEthnicity(label);
		}
		goToStep(getNextStepAfter(stepKey));
	};

	const confirmAge = () => {
		goToStep('hairstyle');
	};

	const confirmFinal = () => {
		setPortraitFile(null);
		setPortraitPrompts(null);
		goToStep('generating');
	};

	const goBack = () => {
		if (step === 'result' || step === 'generating') {
			setPortraitFile(null);
			setPortraitPrompts(null);
			goToStep('final');
			return;
		}

		if (flowIndex <= 0) return;
		goToStep(STUDIO_UI_FLOW[flowIndex - 1]);
	};

	const updateFinalDetail = (key, value) => {
		setFinalDetails((prev) => {
			const next = {
				...prev,
				[key]: key === 'name' ? sanitizeStudioFirstName(value) : value,
			};

			if (key === 'nationality') {
				next.city = pickRandomCity(value);
			}

			return next;
		});
	};

	const nameOptions = useMemo(() => {
		if (quizOptions?.names?.length) {
			return quizOptions.names.map(sanitizeStudioFirstName);
		}

		return STUDIO_NAME_OPTIONS.map(sanitizeStudioFirstName);
	}, [quizOptions?.names]);

	const randomizeName = () => {
		setFinalDetails((prev) => {
			const currentName = sanitizeStudioFirstName(prev.name);
			const currentIndex = nameOptions.indexOf(currentName);
			const nextIndex =
				currentIndex >= 0 ? (currentIndex + 1) % nameOptions.length : 0;
			return { ...prev, name: nameOptions[nextIndex] };
		});
	};

	const resetWizard = () => {
		setStep('gender');
		setGender('woman');
		setAge(25);
		setSelections({ ...INITIAL_SELECTIONS, gender: 'woman' });
		setFinalDetails(INITIAL_FINAL);
		setPortraitFile(null);
		setPortraitPrompts(null);
		storageManager.studio.clearState();
	};

	const resetStudioStorage = () => {
		storageManager.studio.clearState();
	};

	const resolveQuizOptionId = (options, value) =>
		options?.find((option) => option.value === value)?.id;

	const buildCharacterPayload = () => ({
		style: 'realistic',
		age: ageToDecade(age),
		age_number: age,
		ethnicity: selections.ethnicity,
		eye_color: selections.eyecolor,
		hair_style: selections.hairstyle || selections.haircolor || 'Straight',
		hair_color: selections.haircolor,
		facial: selections.bodytype || 'Balanced',
		hobby: 'Travel',
		occupation: finalDetails.occupation,
		personality: finalDetails.personality,
		marital_status: finalDetails.relationship,
		relationship: finalDetails.relationship,
		voice: finalDetails.voice,
		personality_id: resolveQuizOptionId(
			quizOptions?.personality,
			finalDetails.personality,
		),
		occupation_id: resolveQuizOptionId(
			quizOptions?.occupation,
			finalDetails.occupation,
		),
		relationship_id: resolveQuizOptionId(
			quizOptions?.relationship,
			finalDetails.relationship,
		),
		nationality_id: resolveQuizOptionId(
			quizOptions?.nationality,
			finalDetails.nationality,
		),
		zodiac_id: resolveQuizOptionId(quizOptions?.zodiac, finalDetails.zodiac),
		voice_id: resolveQuizOptionId(quizOptions?.voice, finalDetails.voice),
		kink_ids: (finalDetails.kinks ?? [])
			.map((kink) => resolveQuizOptionId(quizOptions?.kinks, kink))
			.filter(Boolean),
		name: sanitizeStudioFirstName(finalDetails.name) || 'My Softie',
		gender,
		body_type: selections.bodytype,
		breast_size: selections.intimatesize,
		city: finalDetails.city,
		nationality: finalDetails.nationality,
		zodiac: finalDetails.zodiac,
		kinks: finalDetails.kinks,
		portrait_image_id: portraitFile?._id,
		generated_photo_prompt: portraitPrompts?.positive,
		generated_negative_photo_prompt: portraitPrompts?.negative,
		language,
	});

	const totalProgressSteps = STUDIO_UI_FLOW.length;
	const currentProgressStep = flowIndex >= 0 ? flowIndex + 1 : 1;

	return {
		step,
		gender,
		age,
		setAge,
		selections,
		finalDetails,
		portraitFile,
		setPortraitFile,
		portraitPrompts,
		setPortraitPrompts,
		updateFinalDetail,
		randomizeName,
		flowIndex,
		isFirstStep,
		hideNav,
		canGoBack,
		optionsForStep,
		selectGender,
		selectOption,
		confirmAge,
		confirmFinal,
		goBack,
		goToStep,
		buildCharacterPayload,
		resetStudioStorage,
		resetWizard,
		totalProgressSteps,
		currentProgressStep,
	};
};
